import {_} from 'lodash';
import * as ko from "knockout";
import {IMessageData, IObjectData, IOverride, ISyncField} from '../../../index';
import {KOSubscriptionTracker} from "../../../util/KOSubscriptionTracker";
import PromiseRequest from "../../../req/PromiseRequest";
import {SPINNER} from "../../../constants";
import {
    createErrorMessage,
    getObjectDefinition,
    getObjectDisplayName,
    getSyncFieldValue,
    IObjectDefinition
} from "../../../util/misc";
import {global} from "../../../appContext";
import {nsOverride} from "../../override/nsOverride";
import {IDialogOptions} from "../../dialog/index";
import {ButtonManager} from "../../../util/ButtonManager";
import {getCancelButton, getContinueButton} from "../../../constants/RouteDefinitions";
import getSyncFieldOverride = nsOverride.getSyncFieldOverride;
import getBasicOverride = nsOverride.getBasicOverride;
import syncfield_editHtml from './syncfield.edit.html?raw';

export let template = syncfield_editHtml;

interface IParams {
    object: IObjectData
    syncfield: ISyncField
    dialog: IDialogOptions
    /** Apply createoverride response locally (avoids full object refresh). */
    onCreated?: (created: IOverride, replacedOverrideId?: number) => void
}

/** `createoverride` returns `data: IOverride[]` (often length 1). */
function createdOverrideFromResponse(data: IOverride | IOverride[] | null | undefined): IOverride {
    const created = Array.isArray(data) ? data[0] : data;
    if (!created?.id) {
        throw new Error('createoverride returned no override id');
    }
    return created;
}

export class viewModel {
    private messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    private isReady: KnockoutObservable<boolean> = ko.observable(true);
    private kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    private reqCreate: PromiseRequest = new PromiseRequest('createoverride');
    private reqDelete: PromiseRequest = new PromiseRequest('deleteoverride');
    private reqUpdateField: PromiseRequest = new PromiseRequest('updateobjectfield');
    private syncfield: ISyncField;
    private value: KnockoutObservable<string | null> = ko.observable(null);
    private object: IObjectData;
    private objDef: IObjectDefinition;
    private objName: string;
    private override: IOverride | null;
    private readonly pageButtons: ButtonManager = new ButtonManager()

    constructor(private params: IParams) {
        this.syncfield = params.syncfield;
        this.object = params.object;
        this.override = getSyncFieldOverride(this.object, this.syncfield, true);
        this.value(this.override?.value ?? this.syncfield.value ?? null);
        this.objDef = getObjectDefinition(this.object.type.id)!;
        this.objName = getSyncFieldValue(this.objDef.syncfieldDisplayName ?? [], this.object.syncfields) ?? '';

        this.pageButtons.add(getCancelButton(() => this.params.dialog.doClose?.(), {
            label: 'cancel',
            validator: () => this.isReady(),
        }))
        this.pageButtons.add(getContinueButton({
            label: 'update value',
            onClick: () => this.onUpdateClick(),
            enabled: () => this.isValid(),
            validator: () => this.isReady(),
        }))
    }

    private isValid(): boolean {
        const a = this.value() != this.syncfield?.value;
        const b = this.value() != this.override?.value;
        return a && b;
    }

    private onUpdateClick() {
        this.isReady(false);
        this.messageData({message: `((updating|td)) ${SPINNER}`});
        let data = this.getSubmitData();
        data = this.trimData(data)
        const joverride = data;
        const isExtendLife = this.syncfield.name === 'ExtendLife';
        const replacedOverrideId = this.override?.id;
        this.params.dialog.isClosable?.(false);
        Promise.resolve()
            .then(() => {
                if (this.override) {
                    return this.reqDelete.request({id: this.override.id});
                }
            })
            .then(() => {
                if (isExtendLife) {
                    return this.reqUpdateField.request({
                        objectid: this.object.id,
                        fieldname: 'ExtendLife',
                        value: joverride.value ?? '',
                    });
                }
            })
            .then(() => {
                return this.reqCreate
                    .request({
                        joverride: JSON.stringify(joverride),
                    }) as Promise<IOverride | IOverride[]>;
            })
            .then((response) => {
                const created = createdOverrideFromResponse(response);
                global.cacheManager.removeCache('searchobject');
                global.cacheManager.removeCache('getobject');
                this.params.onCreated?.(created, replacedOverrideId);
                global.snackbarData({message: 'update completed'})
                this.params.dialog.isClosable?.(true);
                this.params.dialog.doClose?.();
            })
            .catch((reason: string) => {
                this.messageData(createErrorMessage(String(reason)))
                this.params.dialog.isClosable?.(true);
                this.isReady(true);
            })
        ;
    }

    private getSubmitData(): IOverride {
        const obj = this.object;

        let val: string = this.value() ?? '';

        if (this.syncfield.fieldtype === "DateTime") {
            if (val === null) {
                val = ''
            }
        }

        return _.defaults({
            objectid: obj.id,
            objectsrc: getSyncFieldValue('SourceId', obj.syncfields),
            objecttypeid: obj.type.id,
            sourcename: getObjectDisplayName(obj),
            targetname: this.syncfield.name,
            targetobjectid: this.syncfield.syncfieldid,
            targetobjectsrc: String(this.syncfield.syncfieldid),
            targetobjecttypeid: obj.type.id,
            value: val,
        }, getBasicOverride());
    }

    private trimData(data: IOverride): IOverride {
        return _.defaults({
            value: data.value?.trim?.() ?? data.value,
        }, data);
    }

    dispose() {
        this.kosub.unsuscribeAll();
        this.reqCreate.abort();
        this.reqDelete.abort();
        this.reqUpdateField.abort();
    }
}
